<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'unemployedNewAndActiveList' }"
          marginBottom="5"/>

      <h6 class="element-header">Kundendetails</h6>

      <div class="element-box">
        <UnemployedDetails :unemployed="unemployed"/>
        <CoachingMenu :unemployed="unemployed" :coachingCounts="coachingCounts"/>

        <div class="row mb-2 mt-4" style="padding: 0 10px">
          <div class="col-lg-6">
            <h6>Betreutes Wohnen</h6>
          </div>
          <div class="col-lg-6 text-right">
            <button type="button" class="btn btn-primary mb-2 mr-2"
                    style="padding: 5px 7px;"
                    @click="handleModalForm">
              Neue Maßnahme anlegen
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4">
          <table id="dataTable1" class="table table-striped">
            <thead>
            <tr>
              <th>Status</th>
              <th>Maßnahmenart</th>
              <th>Standort</th>
              <th>Versicherungsnr</th>
              <th>Start</th>
              <th>Ende</th>
              <th>Stand</th>
              <th>Prozentuale Verteilung</th>
              <th>Un fehlt</th>
              <th>Fachkraft</th>
              <th>Fachkraft UE</th>
              <th>Assistenzkraft</th>
              <th>Assistenzkraft UE</th>
              <th>Letzter Termin</th>
              <th>Bericht</th>
              <th>Bearbeiten</th>
              <th style="min-width: 160px;">Kunde aktiv beenden</th>
              <th>Löschen</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="coaching in assistedLivingCoachingList" :key="coaching.id">
              <td>{{ coaching?.statusTranslation }}</td>
              <td>{{ coaching?.operationTypeTranslation }}</td>
              <td>{{ coaching.unemployed?.location?.name }}</td>
              <td>{{ coaching?.unemployed?.insuranceNumber }}</td>
              <td>{{ coaching?.startDate }}</td>
              <td>{{ coaching?.endDate }}</td>
              <td>{{ coaching?.unitsDone }}</td>
              <td>{{ coaching?.unitsDonePercent }} %</td>
              <td style="text-align: center">{{ coaching?.notSignedMeetings }}</td>
              <td>
                <div
                    :class="coaching?.selectedSpecialists.length > 1 ? 'popover-custom pb-2 border-bottom border-primary' : ''">
                  <div class="popover-content position-left">
                    <template v-if="coaching?.selectedSpecialists.length > 1">
                      <span v-for="(data, index) in coaching.selectedSpecialists" :key="index">
                        {{ data.fullName }}<br/>
                      </span>
                    </template>
                  </div>
                  {{ getSelectedCoachesData(coaching.selectedSpecialists) }}
                </div>
              </td>
              <td class="text-right">{{ coaching?.unitsDoneBySpecialist }}</td>
              <td>
                <div
                    :class="coaching?.selectedSupervisors.length > 1 ? 'popover-custom pb-2 border-bottom border-primary' : ''">
                  <div class="popover-content position-left">
                    <template v-if="coaching?.selectedSupervisors.length > 1">
                      <span v-for="(data, index) in coaching.selectedSupervisors" :key="index">
                        {{ data.fullName }}<br/>
                      </span>
                    </template>
                  </div>
                  {{ getSelectedCoachesData(coaching.selectedSupervisors) }}
                </div>
              </td>
              <td class="text-right">{{ coaching?.unitsDoneBySupervisor }}</td>
              <td>{{ coaching?.lastUnit }}</td>
              <td>
                <router-link
                    :to="{ name: 'assistedLivingCoachingFirstInterviewForm', params: { id: coaching.id } }"
                    class="btn btn-primary">
                  Bericht
                </router-link>
              </td>
              <td>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    @click="handleEditModalForm(coaching.id)"
                    style="padding: 5px 7px;">
                  Bearbeiten
                </button>
              </td>
              <td>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    @click="handleTerminationModalForm(coaching.id)"
                    style="padding: 5px 7px;">
                  Kunde aktiv beenden
                </button>
              </td>
              <td class="text-center">
                <button class="btn btn-danger ml-2"
                        @click="handleDeleteModal(coaching?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showModalForm"
          title="Betreutes Wohnen"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          :customWidth="'1200px'"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form
            :assistedLivingCoachingId="assistedLivingCoachingId"
            :unemployedId="unemployed?.id"
            :locationId="unemployed?.location?.id"
            :locationName="unemployed?.location?.name"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showTerminationModalForm"
          title="Kunde aktiv beenden"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-xl"
          :hide-footer="true"
          classFooter="justify-content-center">
        <TerminationForm
            :assistedLivingCoachingId="assistedLivingCoachingId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteAssistedLivingCoaching">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import unemployedService from '@/api/Unemployed';
import assistedLivingCoachingService from '@/api/Unemployed/AssistedLivingCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import Form from '@/views/App/Unemployed/Coaching/AssistedLivingCoaching/Form.vue';
import TerminationForm from '@/views/App/Unemployed/Coaching/AssistedLivingCoaching/TerminationForm.vue';
import CoachingMenu from '@/views/App/Unemployed/Coaching/CoachingMenu.vue';
import UnemployedDetails from '@/views/App/Unemployed/UnemployedDetails.vue';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";

const { t }                      = useI18n();
const route                      = useRoute();
const unemployedId               = route.params.id;
const unemployed                 = ref(null);
const coachingCounts             = ref(null);
const isLoaded                   = ref(false);
const assistedLivingCoachingList = ref([]);
const assistedLivingCoachingId   = ref(null);
const submitForm                 = ref(false);
const showModalForm              = ref(false);
const showTerminationModalForm   = ref(false);
const showDeleteModal            = ref(false);

async function getUnemployedOverviewData() {
  try {
    const { data }       = await unemployedService.getUnemployedOverview(unemployedId);
    unemployed.value     = data.data.unemployed;
    coachingCounts.value = data.data.coachingCounts;

  } catch (error) {
    await ErrorToast(error);
  }
}

async function getAssistedLivingCoachingList() {
  try {
    const { data } = await assistedLivingCoachingService.getAssistedLivingCoachingList(
        unemployedId,
    );

    assistedLivingCoachingList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

watch(submitForm, () => {
  assistedLivingCoachingId.value = null; // Important
  showModalForm.value            = false;
  showTerminationModalForm.value = false;
  showDeleteModal.value          = false;
  getUnemployedOverviewData();
  getAssistedLivingCoachingList();
});

function handleModalForm() {
  CloseToast();
  assistedLivingCoachingId.value = null; // Important
  showModalForm.value            = true;
  showDeleteModal.value          = false;
  showTerminationModalForm.value = false;
}

function handleDeleteModal(id) {
  CloseToast();
  assistedLivingCoachingId.value = id;
  showDeleteModal.value          = true;
  showModalForm.value            = false;
  showTerminationModalForm.value = false;
}

function handleEditModalForm(id) {
  CloseToast();
  assistedLivingCoachingId.value = id;
  showModalForm.value            = true;
  showDeleteModal.value          = false;
  showTerminationModalForm.value = false;
}

function handleTerminationModalForm(id) {
  CloseToast();
  assistedLivingCoachingId.value = id;
  showTerminationModalForm.value = true;
  showModalForm.value            = false;
  showDeleteModal.value          = false;
}

async function deleteAssistedLivingCoaching() {
  try {
    await assistedLivingCoachingService.deleteAssistedLivingCoaching(+assistedLivingCoachingId.value);

    assistedLivingCoachingId.value = null;
    showDeleteModal.value          = false;

    await getUnemployedOverviewData();
    await getAssistedLivingCoachingList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function getSelectedCoachesData(data) {

  if (Array.isArray(data) && data.length === 1) {
    return data[0]?.fullName;
  }

  if (Array.isArray(data) && data.length > 1) {
    return 'Mehrere';
  }

  return '';
}

onMounted(async () => {
  CloseToast();

  await Promise.all([
    getUnemployedOverviewData(),
    getAssistedLivingCoachingList(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>